import React, {
  forwardRef,
  Key,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import styles from './Message.scss';
import cls from 'classnames';

import noop from 'lodash/noop';

import { DEFAULT_DURATION } from '../../../../constants';
import { InnerMessageConfig } from '../../../../types';
import { useTouchEvents } from './hooks/useTouchEvents';

type Props = {
  config: InnerMessageConfig;
  onClose: (key: Key) => void;
  isTopPlacement: boolean;
};

const PIXELS_TO_CLOSE = 50;

export const Message = forwardRef<HTMLDivElement, Props>(
  ({ config, onClose: onInternalClose, isTopPlacement }, ref) => {
    const {
      type,
      icon,
      style,
      className,
      childrenClassNames = {},
      duration = DEFAULT_DURATION,
      content,
      onClose = noop,
      timesUpdated,
      key,
    } = config;

    const [isHovered, setIsHovered] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const touchStartPositionY = useRef(0);
    const touchEndPositionY = useRef(0);

    const handleTouchStart = useCallback((event: TouchEvent) => {
      touchStartPositionY.current = event.touches[0].pageY;
    }, []);

    const handleTouchMove = useCallback((event: TouchEvent) => {
      touchEndPositionY.current = event.touches[0].pageY;
    }, []);

    const handleTouchEnd = useCallback(() => {
      const diff = touchEndPositionY.current - touchStartPositionY.current;

      if (isTopPlacement ? diff < -PIXELS_TO_CLOSE : diff > PIXELS_TO_CLOSE) {
        onClose(key);
        onInternalClose(key);
      }
    }, [key, onClose, onInternalClose]);

    useTouchEvents({
      container: containerRef.current,
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    });

    useEffect(() => {
      if (duration > 0 && duration !== Infinity && !isHovered) {
        const timeout = setTimeout(() => {
          onClose(key);
          onInternalClose(key);
        }, duration);

        return () => {
          clearTimeout(timeout);
        };
      }
    }, [duration, timesUpdated, isHovered]);

    return (
      <div
        data-locator={`message-wrapper-${key}${type ? `-${type}` : ''}`}
        ref={(node) => {
          if (node) {
            if (ref) {
              (ref as MutableRefObject<HTMLDivElement>).current = node;
            }

            (containerRef as MutableRefObject<HTMLDivElement>).current = node;
          }
        }}
        className={cls(
          styles.Message,
          className,
          type && styles[type],
          isTopPlacement && styles.isTopPlacement
        )}
        style={style}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className={cls(styles.container, childrenClassNames.container)}>
          {icon && (
            <div
              className={cls(
                styles.iconWrapper,
                childrenClassNames.iconWrapper,
                type && styles[type]
              )}
            >
              {icon}
            </div>
          )}
          <div
            data-locator={`message-content-${key}${type ? `-${type}` : ''}`}
            className={cls(styles.content, childrenClassNames.content)}
          >
            {content}
          </div>
        </div>
      </div>
    );
  }
);
